import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  isDevMode,
  signal,
  viewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ConsentDialogResult,
  ConsentFormData,
  ConsentFormDialogData
} from '@consent/models/consent-form.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-consent-form-dialog',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './consent-form-dialog.component.html'
})
export class ConsentFormDialogComponent {
  private readonly config =
    inject<DynamicDialogConfig<ConsentFormDialogData>>(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly sanitizer = inject(DomSanitizer);

  /** Live reference to the sandboxed iframe, used to authenticate messages. */
  private readonly frame =
    viewChild<ElementRef<HTMLIFrameElement>>('consentFrame');

  readonly form = signal<ConsentFormData | null>(
    this.config.data?.form ?? null
  );

  /** Set once we've acted on a message, so a flood of posts can't double-fire. */
  private handled = false;

  /**
   * Bridges the sandboxed iframe → host app. The injected click-bridge signals
   * accept with `window.parent.postMessage('accept', <host origin>)` (targeted,
   * never `'*'` — see {@link buildHeadInjection}); we close the dialog with an
   * accepted result and let {@link ConsentFormService} make the authenticated API
   * call (the iframe never holds tokens or authoritative IDs).
   *
   * `window` receives EVERY postMessage (extensions, libs, other frames), so we
   * only act when `event.source` is our own iframe's `contentWindow`. (Origin is
   * the opaque string "null" for a no-`allow-same-origin` sandbox, so origin
   * allow-listing is useless here — source identity is the real check.)
   */
  @HostListener('window:message', ['$event'])
  protected onWindowMessage(event: MessageEvent): void {
    if (this.handled) {
      return;
    }

    const frameWindow = this.frame()?.nativeElement.contentWindow;
    if (!frameWindow || event.source !== frameWindow) {
      return; // not from our iframe
    }

    if (event.data === 'accept') {
      this.handled = true;
      const result: ConsentDialogResult = { accepted: true };
      this.dialogRef.close(result);
    }
  }

  /**
   * `custom_body` is a complete, UNTRUSTED admin-authored HTML document (its own
   * card, copy and action buttons). It is rendered verbatim inside a sandboxed
   * `<iframe srcdoc>` — see template. The security boundary is the iframe
   * `sandbox` attribute (a unique opaque origin with no `allow-same-origin`),
   * NOT Angular's HTML sanitizer.
   *
   * We therefore mark the value trusted for the srcdoc sink on purpose: running
   * it through the sanitizer would strip the `<style>`/`<script>` and destroy the
   * admin's design, while adding no real protection here (the iframe already
   * isolates it from our cookies, storage and DOM).
   */
  readonly trustedBody = computed<SafeHtml | null>(() => {
    const body = this.form()?.custom_body;
    if (!body) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.enhanceDocument(body));
  });

  /**
   * Injects our own additions into the (untrusted) consent document before it is
   * handed to the iframe `srcdoc`:
   *
   *  1. A CSS reset — admin templates ship their own page background on
   *     `html`/`body` and top-align the card. Inside our oversized, chrome-less
   *     iframe that leaks a visible "box" and pins the card to the top. We force
   *     the page transparent and centre the card with `margin:auto` (overflow-safe,
   *     so a taller-than-viewport card still scrolls — unlike `justify-content`).
   *
   *  2. A click-bridge script — the Accept button lives inside the sandboxed
   *     iframe, so the host app cannot attach a handler to it. Rather than depend
   *     on the (backend-authored) template calling `postMessage` itself, WE wire it:
   *     a capture-phase click listener detects the accept action and posts
   *     `'accept'` to the parent, which {@link onWindowMessage} validates. The
   *     template needs no changes. The accept element is matched by the REQUIRED
   *     `data-consent-action="accept"` attribute; a label-based heuristic is kept
   *     ONLY as a dev-mode fallback (see {@link buildHeadInjection}).
   *
   * Both run inside the sandbox (`allow-scripts`), which still isolates the doc
   * from our cookies/storage/DOM — injecting our own trusted snippet doesn't
   * weaken that boundary. Inserted as the LAST `<head>` children so they win over
   * the document's own rules; falls back to `<html>` or a prepend when no `<head>`.
   */
  private enhanceDocument(html: string): string {
    // The sandboxed iframe has an opaque "null" origin and cannot read the host
    // origin itself, so we capture it here (host side) and pass it to the bridge
    // as the postMessage target origin — see {@link buildHeadInjection}.
    //
    // The bridge is served from the app origin; resolve its absolute URL against
    // the host document's <base> (document.baseURI) so it works both at root and
    // under a subpath deployment (e.g. /issuer/).
    const bridgeSrc = new URL('consent-bridge.js', document.baseURI).href;
    const injection = ConsentFormDialogComponent.buildHeadInjection(
      bridgeSrc,
      window.location.origin,
      isDevMode()
    );

    if (/<\/head>/i.test(html)) {
      return html.replace(/<\/head>/i, `${injection}</head>`);
    }
    if (/<html[^>]*>/i.test(html)) {
      return html.replace(/<html[^>]*>/i, (match) => `${match}${injection}`);
    }
    return `${injection}${html}`;
  }

  /**
   * Builds the style reset + click-bridge injected into every consent document.
   *
   * @param appOrigin the HOST app origin (`window.location.origin`), used as the
   *   `postMessage` TARGET ORIGIN so the accept signal is delivered ONLY to our
   *   app. A wildcard `'*'` would hand the message to ANY frame embedding this
   *   document (e.g. a phishing page that iframes the consent URL); the sandboxed
   *   iframe can't read the parent origin itself, so the host injects it here.
   * @param dev whether the app is in dev mode (`isDevMode()`); gates the
   *   label-based heuristic and its diagnostic warning to development only.
   *
   * Accept matching: the REQUIRED contract is `data-consent-action="accept"` on
   * the accept control (document this in the Consent Management backend service
   * template API). In production ONLY that explicit attribute fires the bridge.
   * In dev, a label heuristic is kept as a fallback and a warning is logged when
   * a button click matches neither the attribute nor the heuristic — surfacing
   * templates that forgot the attribute before they reach production.
   *
   * CSP: the bridge is loaded as an EXTERNAL script from the app origin
   * (`consent-bridge.js`), NOT inlined. A srcdoc frame inherits the host page's
   * Content-Security-Policy, and `'self'` in that policy resolves to the app
   * origin — so a strict `script-src 'self'` (no `'unsafe-inline'`) permits this
   * bridge while still blocking the untrusted admin template's own inline
   * scripts. That is exactly the boundary we want: our trusted, app-served bridge
   * runs; injected/admin scripts do not. Config (`data-origin`, `data-dev`) is
   * passed via data-* attributes so the served file stays byte-identical across
   * environments (no per-env hash/nonce to maintain).
   *
   * The inline `<style>` reset below is allowed by `style-src 'unsafe-inline'`
   * (styles are far lower risk than scripts); keep it inline.
   */
  private static buildHeadInjection(
    bridgeSrc: string,
    appOrigin: string,
    dev: boolean
  ): string {
    const src = ConsentFormDialogComponent.escapeHtmlAttribute(bridgeSrc);
    const origin = ConsentFormDialogComponent.escapeHtmlAttribute(appOrigin);
    return (
      '<style>' +
      'html,body{background:transparent!important;margin:0!important}' +
      'body{min-height:100vh;display:flex}' +
      'body>*{margin:auto!important}' +
      // Disabled accept control (gated by data-consent-require="all"): the admin
      // template's own button CSS usually has no :disabled rule, so give a
      // consistent, non-interactive appearance while the gate is unmet.
      '[data-consent-action="accept"]:disabled{opacity:.55!important;' +
      'cursor:not-allowed!important;pointer-events:none!important}' +
      '</style>' +
      `<script src="${src}" data-origin="${origin}" data-dev="${
        dev ? 'true' : 'false'
      }"></script>`
    );
  }

  /** Escapes a value for safe interpolation into an HTML attribute (double-quoted). */
  private static escapeHtmlAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
